import React, { useState, useCallback } from 'react';
import { IconButton, Menu, MenuItem, Typography, Box, Tooltip } from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import { useLanguage } from '../../contexts/LanguageContext';
import i18n from '../../i18n';

interface LanguageOption {
  code: string;
  label: string;
  flag: string;
}

const languages: LanguageOption[] = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
];

const LanguageSwitcher: React.FC = () => {
  const { language, changeLanguage, t } = useLanguage();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const currentLanguage = language?.split('-')[0] || 'en';

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleLanguageChange = useCallback(async (lang: string) => {
    if (lang === currentLanguage) {
      handleClose();
      return;
    }
    
    try {
      await changeLanguage(lang);
      // Language change is handled by the LanguageContext and i18n
      // No need to reload the page as it clears authentication state
      console.log(`Language changed to: ${lang}`);
    } catch (error) {
      console.error('Failed to change language:', error);
    } finally {
      handleClose();
    }
  }, [changeLanguage, currentLanguage, handleClose]);

  const currentLang = languages.find(lang => lang.code === currentLanguage) || languages[0];

  return (
    <Box>
      <Tooltip title={t('common.changeLanguage')}>
        <IconButton
          color="inherit"
          aria-label="change language"
          onClick={handleClick}
          size="large"
          sx={{
            borderRadius: 1,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <Box display="flex" alignItems="center">
            <TranslateIcon sx={{ mr: 0.5 }} />
            <Typography variant="body2" sx={{ textTransform: 'uppercase' }}>
              {currentLang.flag} {currentLanguage}
            </Typography>
          </Box>
        </IconButton>
      </Tooltip>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 150,
          },
        }}
      >
        {languages.map((lang) => (
          <MenuItem 
            key={lang.code} 
            onClick={() => handleLanguageChange(lang.code)}
            selected={currentLanguage === lang.code}
            sx={{
              '&.Mui-selected': {
                backgroundColor: 'action.selected',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              },
            }}
          >
            <Box display="flex" alignItems="center" width="100%">
              <Box component="span" sx={{ mr: 1.5, fontSize: '1.1rem' }}>
                {lang.flag}
              </Box>
              <Typography variant="body1">
                {lang.label}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default LanguageSwitcher;